import { useEffect, useState } from 'react'
import { ViewState } from '@devexpress/dx-react-scheduler'
import moment from 'moment'
import { useTranslation } from 'react-i18next'
import calendarStyles from '../../styles/EventsCalendarStyles'
import { IRootState } from '../../../../../redux/types'
import { getCalendarEvents } from '../../../../../redux/actions/events'
import HomeCalendarSkeleton from '../../../../shared/components/skeletons/HomeCalendarSkeleton'
import { useDispatch, useSelector } from 'react-redux'
import { useLocation, useNavigate } from 'react-router'
import { Box, Container, Grid, Paper, Typography, useTheme } from '@mui/material'
import {
  AllDayPanel,
  AppointmentTooltip,
  Appointments,
  CurrentTimeIndicator,
  DateNavigator,
  DayView,
  MonthView,
  Scheduler,
  TodayButton,
  Toolbar,
  ViewSwitcher,
  WeekView
} from '@devexpress/dx-react-scheduler-material-ui'
import {
  CustomHeader,
  tableBodyMonth,
  TimeIndicator,
  Appointment,
  LayoutTooltip,
  tableDayWeek,
  tableBodyWeek,
  tableCellMonth,
  tableDayMonth,
  TimeTableCell,
  Content
} from './calendar';

type EventCalendarProps = {
  family_id?: null | number
  brand_id?: null | number
  fullHeight?: null | boolean
}

const calendarLanguages = ['es', 'es', 'en', 'fr', 'de', 'ca']

export default function EventCalendar({ family_id = null, brand_id = null }: EventCalendarProps) {
  const { t } = useTranslation(['global'])
  const theme = useTheme()
  const classes = calendarStyles(theme, {
    top: 0,
    shadedHeight: 0,
    backgroundColor: '',
    color: ''
  })

  let location = useLocation()
  const navigate = useNavigate()

  const dispatch = useDispatch()
  const { calendarEvents, loading } = useSelector((state: IRootState) => state.event)
  const { globalCountryId, globalLanguageId, globalRoleId } = useSelector((state: IRootState) => state.global)
  const [currentLanguage, setCurrentLanguage] = useState('es')
  const [calendarDate, setCalendarDate] = useState(moment())
  const currentDate = new Date()

  useEffect(() => {
    dispatch(
      getCalendarEvents({
        currentDate: calendarDate,
        family_id,
        brand_id,
        country_id: globalCountryId,
        language_id: globalLanguageId,
        role_id: globalRoleId
      })
    )
  }, [calendarDate, globalCountryId, globalLanguageId, globalRoleId, family_id, brand_id])

  useEffect(() => {
    if (globalLanguageId != null) setCurrentLanguage(calendarLanguages[globalLanguageId])
  }, [globalLanguageId])

  let hidenHeader = location.pathname === '/eventos'

  return (
    <>
      {!hidenHeader && (
        <Grid item xs={12} sx={classes.header}>
          <Box display="flex" justifyContent="space-between" alignItems="center">
            <Typography variant="h5">{t('events')}</Typography>
            <Typography
              onClick={() => {
                window.scrollTo({ top: 0, left: 0, behavior: 'smooth' })
                navigate(`/eventos`)
              }}
              color="textSecondary"
              sx={classes.seeMore}
            >
              {t('see-more')}
            </Typography>
          </Box>
        </Grid>
      )}
      <Box sx={{ display: loading ? 'block' : 'none' }}>
        <HomeCalendarSkeleton />
      </Box>
      <Container maxWidth="lg" sx={{ paddingY: 0 }}>
        <Paper elevation={3} style={{ display: loading ? 'none' : 'block' }}>
          <Scheduler data={calendarEvents} locale={currentLanguage}>
            <ViewState
              defaultCurrentDate={currentDate}
              defaultCurrentViewName={'Month'}
              onCurrentDateChange={(currentDate: any) => {
                setCalendarDate(currentDate)
                dispatch(
                  getCalendarEvents({
                    currentDate,
                    family_id,
                    brand_id,
                    country_id: globalCountryId,
                    language_id: globalLanguageId,
                    role_id: globalRoleId
                  })
                )
              }}
            />

            <DayView displayName={t('days')} startDayHour={8} endDayHour={18} />
            <WeekView
              displayName={t('week')}
              startDayHour={8}
              endDayHour={18}
              timeTableCellComponent={TimeTableCell}
              timeTableLayoutComponent={tableBodyWeek}
              dayScaleLayoutComponent={tableDayWeek}
            />
            {location.pathname === '/eventos' ? (
              <MonthView displayName={t('month')} timeTableLayoutComponent={tableBodyMonth} dayScaleLayoutComponent={tableDayMonth} />
            ) : (
              <MonthView
                displayName={t('month')}
                timeTableLayoutComponent={tableBodyMonth}
                dayScaleLayoutComponent={tableDayMonth}
                timeTableCellComponent={tableCellMonth}
              />
            )}

            <Toolbar />
            <ViewSwitcher />
            <DateNavigator />
            <TodayButton />

            <Appointments appointmentComponent={Appointment} />
            <AppointmentTooltip headerComponent={CustomHeader} contentComponent={Content} showCloseButton layoutComponent={LayoutTooltip} />

            <CurrentTimeIndicator indicatorComponent={TimeIndicator} shadePreviousCells shadePreviousAppointments />

            <AllDayPanel />
          </Scheduler>
        </Paper>
      </Container>
    </>
  )
}